// wix code and blocks viewer apps expects regeneratorRuntime to be defined :/
// https://wix.slack.com/archives/CKDB50KE2/p1655630884212889
// https://wix.slack.com/archives/CGJREGM7B/p1653330082949059
import 'regenerator-runtime/runtime'

import _ from 'lodash'
import { amdLoaderFactory } from '@wix/thunderbolt-renderer-utils'
import type { ScriptCache, ModuleLoader } from '@wix/thunderbolt-renderer-utils'
import type { PlatformPerformanceStoreType } from './bi/PlatformPerformanceStore'

export type PreloadStats = { hit: number; miss: number }

declare let self: DedicatedWorkerGlobalScope & {
	define?: ((
		nameOrDependencies: string | Array<string>,
		dependenciesOrFactory: Array<string> | Function,
		factory?: Function
	) => void) & { amd?: boolean }
}

export default function ({
	scriptsCache,
	globalThis = self,
	fetch = self.fetch,
	platformPerformanceStore,
}: {
	scriptsCache: ScriptCache
	globalThis?: any
	fetch?: typeof self.fetch
	platformPerformanceStore?: PlatformPerformanceStoreType
}): ModuleLoader {
	const defaultDependencies: { [name: string]: unknown } = {
		lodash: _,
		_,
		'wix-data': { default: { dsn: 'https://b58591105c1c42be95f1e7a3d5b3755e@sentry.io/286440' } },
	}

	// Expose lodash on the global scope for non-AMD platform scripts that reference
	// bare `_`. In Yoshi/webpack, lodash UMD takes the AMD path and sets root._ automatically.
	// Rspack's `typeof define: 'undefined'` forces the CommonJS path which skips root._.
	globalThis._ = _

	// Split each real module load into contiguous fetch and eval windows (fetch end == eval start) and
	// record them on the store our SSR phase metrics ship, so they roll up into
	// platform_script_fetch_total / platform_script_eval_total. These per-request aggregates are what the
	// per-filename appScriptFetch_<filename> METERs can't give — those are quantile-only, one series per
	// script, and not summable into a total. `script_fetch` here is wall time as the isolate sees it:
	// host fetch + cross-boundary transfer; `script_eval` is the V8 compile + top-level exec.
	const onModuleLoadTiming = platformPerformanceStore
		? ({
				fetchStartTime,
				fetchEndTime,
				evalEndTime,
			}: {
				fetchStartTime: number
				fetchEndTime: number
				evalEndTime: number
			}) => {
				platformPerformanceStore.addPlatformPerformanceEvent('script_fetch started', fetchStartTime)
				platformPerformanceStore.addPlatformPerformanceEvent('script_fetch ended', fetchEndTime)
				platformPerformanceStore.addPlatformPerformanceEvent('script_eval started', fetchEndTime)
				platformPerformanceStore.addPlatformPerformanceEvent('script_eval ended', evalEndTime)
			}
		: undefined

	// fetch defaults to the isolate's global fetch bridge; the SSR preload path injects a mock
	// fetch that serves script bodies already present in the main thread's fetchCache.
	const loader = amdLoaderFactory({ scriptsCache, defaultDependencies, globalThis, fetch, onModuleLoadTiming })

	// Instrument request-time loads to measure preload effectiveness. stats is set only after
	// _preloadScripts finishes, so preload's own loads (and the client worker, which never sets it)
	// are not counted. A load whose URL was preloaded resolves from scriptsCache; the rest pay the
	// full fetch + eval. Emitted as METERs host-side in platformSsr.ts.
	const stats = globalThis.__preloadStats as PreloadStats | undefined
	if (!stats) {
		return loader
	}

	const preloadedUrls = globalThis.__preloadedScriptUrls as Set<string> | undefined
	const originalLoadModule = loader.loadModule
	loader.loadModule = async function trackedLoadModule<T>(
		url: string,
		dependenciesMap?: { [name: string]: unknown },
		transform?: (code: string, url: string) => Promise<string>
	): Promise<T> {
		const wasPreloaded = preloadedUrls?.has(url) ?? false
		try {
			return await originalLoadModule<T>(url, dependenciesMap, transform)
		} finally {
			if (wasPreloaded) {
				stats.hit += 1
			} else {
				stats.miss += 1
			}
		}
	}

	return loader
}
